/**
 * Feature E02-F02-T04 — Stage stepper with current-stage highlight + preview banner.
 *
 * The CONSUMER of the generic `ui/Stepper` primitive (E10-F02-T07). The primitive
 * is presentation-only and explicitly leaves two things to this feature
 * (Stepper.tsx doc):
 *   - the stage-5→4 COLLAPSE (the DB carries 1..5; "Launched" (5) shares the last
 *     step) — done here via `stageToStepIndex`;
 *   - the PREVIEW banner: clicking a non-current step PREVIEWS that stage's
 *     content WITHOUT mutating `project.stage`, showing "Viewing stage X" with a
 *     "Back to current" button (legacy `setProjectStageView`).
 *
 * This wrapper owns the local `preview` state and the banner; it passes the 4
 * collapsed steps + the mapped `current`/`preview` indices down to the primitive.
 * Selecting a stage calls `onStageView(index)` so the parent can swap the body
 * content shown for that stage (the real stage content lives in the per-stage
 * features, e.g. Kick-off); "Back to current" clears the preview.
 */
import { useState, type JSX } from 'react';
import { Stepper } from '../ui/index.js';
import {
  BACK_TO_CURRENT_LABEL,
  STAGE_LABELS,
  previewBanner,
  stageToStepIndex,
} from './projectDetailModel.js';

export interface StageStepperProps {
  /** The project's CURRENT 1-based DB stage (1..5, nullable). */
  readonly stage: number | null | undefined;
  /**
   * Notified when the previewed stage changes (a 0-based step index, or `null`
   * when reset to the current stage). The parent uses it to swap the stage body
   * content shown; the real `project.stage` is NEVER changed by a preview.
   */
  readonly onStageView?: (stepIndex: number | null) => void;
  /** Test-id prefix (the inner primitive becomes `<testId>-stepper`). */
  readonly testId?: string;
}

export function StageStepper({
  stage,
  onStageView,
  testId = 'stage',
}: StageStepperProps): JSX.Element {
  const current = stageToStepIndex(stage);
  // The stage being previewed (null = viewing the current stage).
  const [preview, setPreview] = useState<number | null>(null);

  const banner = previewBanner(current, preview);

  function selectStep(index: number): void {
    // Selecting the current step is a reset; any other step is a preview.
    const next = index === current ? null : index;
    setPreview(next);
    onStageView?.(next);
  }

  function backToCurrent(): void {
    setPreview(null);
    onStageView?.(null);
  }

  const steps = STAGE_LABELS.map((label) => ({ label }));

  return (
    <div data-testid={`${testId}-stage-stepper`}>
      <Stepper
        steps={steps}
        current={current}
        preview={preview}
        onStepClick={selectStep}
        testId={`${testId}-stepper`}
        ariaLabel="Project stages"
      />
      {banner.visible ? (
        <div
          data-testid={`${testId}-preview-banner`}
          role="status"
          className="mt-3 flex items-center justify-between rounded border border-blue-200 bg-blue-50 px-3 py-2 text-xs text-blue-800"
        >
          {/* Legacy renderStageContent banner (project.js ~L761-764): names the
              previewed stage AND the real current stage, not a bare number. */}
          <span data-testid={`${testId}-preview-label`}>
            <i className="fa-solid fa-eye mr-1" aria-hidden />
            Viewing stage <strong>{banner.label}</strong>. Current project stage:{' '}
            <strong>{banner.currentLabel}</strong>
          </span>
          <button
            type="button"
            data-testid={`${testId}-back-to-current`}
            onClick={backToCurrent}
            className="font-medium text-blue-700 underline hover:text-blue-900"
          >
            <i className="fa-solid fa-arrow-left mr-1" aria-hidden />
            {BACK_TO_CURRENT_LABEL}
          </button>
        </div>
      ) : null}
    </div>
  );
}

export default StageStepper;
